import Backbone from "backbone";
import * as logger from "../utils/logger";
import { dispatcher, DispatcherEvents } from "../utils/dispatcher";
import idSerializer from "geotab-uuid";
import storage, { StorageKeys } from "../utils/storage/storage";
import { handleApi, handleJSNative } from "../utils/error";
import { CancelablePromise as Promise } from "../utils/cancelablePromise";
import PromiseQueue from "../utils/promiseQueue";
import UserHosRuleSetSync from "./userHosRuleSet";
import Api from "../services/api";
import Service from "../services/base";
import HosProcess from "../services/hosProcess";
import HosSync from "./hos";
import MediaSync from "./media";
import { hasMediaPendingDelivery } from "../model/Image";
import CoachingSync from "./coachingSync";
import DvirSync from "./dvir";
import ShipmentSync from "./shipment";
import TrailerAttachmentSync from "./trailerAttachment";
import MessagesSync from "./messages";
import { IMiscSync, syncDefectLists } from "./misc";
import DriverChangeSync from "./driverChange";
import MalfunctionsHelper from "./malfunctions";
import DutyStatusLog, { IAnnotation, IDutyStatusLog, NODEVICEID } from "../model/DutyStatusLog";
import { getCurrentVehicle, isNoVehicle } from "../utils/device";
import { FeatureFlagName } from "../utils/featureFlagName";
import { FeedTypeKey, IAddressSearch, populateAddressesRedux, removeFeedVersion, TSyncCallback } from "./common";
import { extend } from "../utils/vanilla";
import { IHashMap, SyncType, SyncQueueGroup, SYNC_TYPE_TO_QUEUE_GROUP, QUEUE_GROUP_TO_SYNC_TYPES } from "./sync.types";
import { needsSync as needsSyncUtil } from "./syncUtils";
import { dslSpamDetector } from "./spamDetector";
import TranslationService from "../services/translation";
import { IShipment, Shipment } from "../model/Shipment";
import { TViolationValidationResult } from "../utils/violations";
import { Device } from "../model/Device";
import { ITrailer, Trailer } from "../model/Trailer";
import { DriveStore } from "../store/reducers";
import { IMessage } from "../model/Message";
import { SecurityService } from "../utils/security";
import { UserService } from "../utils/users";
import { DutyStatusLogService } from "../model/DutyStatusLogs";
import { HosLogActions } from "../store/hosLogs/actions";
import { HosAnnotationActions } from "../store/hosAnnotations/actions";
import { UserHosRuleSetActions } from "../store/userHosRuleSet/actions";
import { IApiError, ITransportError } from "../utils/api.interfaces";
import TripSync from "./trip";
import { getTripStartOfDay, TripActions } from "../store/trips/actions";
import Trip from "../model/Trip";
import TripAnnotationSync from "./tripAnnotation";
import TripAnnotation from "../model/TripAnnotation";
import { TripAnnotationActions } from "../store/tripAnnotations/actions";
import GroupLinkedDeviceSync from "./groupLinkedDevice";
import GroupLinkedDevice from "../model/GroupLinkedDevice";
import { GroupLinkedDeviceActions } from "../store/groupLinkedDevice/actions";
import { ISyncInterval } from "../store/mobile/types";
import { DVIRLogsService } from "../model/DVIRLogsService";
import { DvirLogActions } from "../store/dvirLogs/actions";
import { MessageActions } from "../store/messages/actions";
import { DriverChangesActions } from "../store/driverChanges/actions";
import { TrailerAttachmentActions } from "../store/trailerAttachments/actions";
import { ShipmentActions } from "../store/shipment/actions";
import { isVehicleRemoved } from "../utils/errorHandler/error.messages";
import { ISyncOptions, TSyncSource } from "./base";
import { ITrailerAttachment, TrailerAttachment } from "../model/TrailerAttachment";
import { IDVIRLog } from "../model/DVIRLog";
import UserHosRuleSet, { IUserHosRuleSet } from "../model/UserHosRuleSet";
import { IFeatureFlagsService } from "../services/featureFlags";
import { formatLocalDate } from "../utils/date";
import { getRuleSetName } from "hos-tools";
import { getServerDate } from "../utils/storage/date";
import Context from "../app.context";
import { MeasureName } from "../utils/performanceProfiler/measureName";
import PerformanceProfileBatcher from "../utils/performanceProfiler/performanceProfileBatcher";
import constants, { MINUTES } from "../constants";
import { uploadEventLogsFromIndexedDb, getUploadChunkSize } from "./eventLogs";

const syncTimeout = 2 * 60 * 1000;

const syncGroups = [
    SyncQueueGroup.HOS,
    SyncQueueGroup.DVIR,
    SyncQueueGroup.SHIPMENTS,
    SyncQueueGroup.MESSAGES,
    SyncQueueGroup.DRIVER_CHANGE,
    SyncQueueGroup.TRIP,
    SyncQueueGroup.COACHING
];

type TSyncApiType = StorageKeys.HOSANNOTATIONS_KEY | StorageKeys.SHIPMENTS_KEY | "trailers" | StorageKeys.TRAILER_ATTACHMENTS_KEY | StorageKeys.DVIR_KEY |
    StorageKeys.USER_HOS_RULESETS_KEY | StorageKeys.USER_LOGS_KEY | StorageKeys.DRIVER_CHANGES_KEY | StorageKeys.USER_TRIPS_KEY | StorageKeys.TRIPANNOTATIONS_KEY |
    StorageKeys.GROUPLINKEDDEVICES_KEY | StorageKeys.MESSAGES_KEY | StorageKeys.SYSTEM_LOGS_KEY;

// Sync set used at login / when no vehicle is selected. Avoids hos sync (done at vehicle
// selection). Includes Coaching because coaching comments are driver-scoped and
// must still flush without a vehicle, else they stay stuck on "Sending..." (MYG-132907).
export const syncsForLogin = [SyncType.Messages, SyncType.UserHosRuleSet, SyncType.Coaching];

export default class Sync extends Service {
    private readonly hosSync: HosSync;
    private readonly dvirSync: DvirSync;
    private readonly mediaSync: MediaSync;
    private readonly coachingSync: CoachingSync;
    private readonly shipmentSync: ShipmentSync;
    private readonly trailerAttachmentSync: TrailerAttachmentSync;
    private readonly messagesSync: MessagesSync;
    private readonly userHosRuleSetSync: UserHosRuleSetSync;
    private readonly driverChangeSync: DriverChangeSync;
    private readonly tripSync: TripSync;
    private readonly tripAnnotationSync: TripAnnotationSync;
    private readonly groupLinkedDeviceSync: GroupLinkedDeviceSync;
    private readonly profilingBatcher: PerformanceProfileBatcher;

    private readonly promiseQueues: Map<SyncQueueGroup, PromiseQueue>;

    constructor(
        private readonly api: Api,
        private readonly translationService: TranslationService,
        private readonly securityService: SecurityService,
        private readonly userService: UserService,
        private readonly dutyStatusLogService: DutyStatusLogService,
        private readonly dvirLogsService: DVIRLogsService,
        private readonly store: DriveStore,
        private readonly hosProcess: HosProcess,
        private readonly featureFlagsService: IFeatureFlagsService,
        private readonly getCachedContext: (userName: string) => Context | undefined
    ) {
        super();

        this.promiseQueues = new Map();
        syncGroups.forEach(group => {
            this.promiseQueues.set(group, new PromiseQueue(
                msg => { logger.log(logger.Source.SYNC, `PromiseQueue [${ group }] ${ msg }`); },
                msg => { logger.error(logger.Source.SYNC, `PromiseQueue Error [${ group }] ${ msg }`); },
                handleJSNative
            ));
        });

        let syncOptions: ISyncOptions = {
            api: this.api,
            hosProcess: this.hosProcess,
            translator: this.translationService.getTranslator(),
            store: this.store,
            securityService: this.securityService,
            userService: this.userService,
            featureFlagsService: this.featureFlagsService,
            getCachedContext: this.getCachedContext
        };
        const malfunctionsHelper = new MalfunctionsHelper(syncOptions, dutyStatusLogService);
        this.hosSync = new HosSync(syncOptions, this.dutyStatusLogService, malfunctionsHelper);
        this.dvirSync = new DvirSync(syncOptions, this.dvirLogsService);
        this.mediaSync = new MediaSync(syncOptions);
        this.coachingSync = new CoachingSync(syncOptions);
        this.shipmentSync = new ShipmentSync(syncOptions);
        this.trailerAttachmentSync = new TrailerAttachmentSync(syncOptions);
        this.messagesSync = new MessagesSync(syncOptions, this.dutyStatusLogService);
        this.userHosRuleSetSync = new UserHosRuleSetSync(syncOptions);
        this.driverChangeSync = new DriverChangeSync(syncOptions);
        this.tripSync = new TripSync(syncOptions);
        this.tripAnnotationSync = new TripAnnotationSync(syncOptions);
        this.groupLinkedDeviceSync = new GroupLinkedDeviceSync(syncOptions);
        this.profilingBatcher = new PerformanceProfileBatcher(MeasureName.Sync);
    }

    get dutyStatusLogs() {
        return this.getItemApiWithRemove<IDutyStatusLog>(StorageKeys.USER_LOGS_KEY);
    }

    get annotations() {
        return this.getItemApi<IAnnotation>(StorageKeys.HOSANNOTATIONS_KEY);
    }

    get shipments() {
        return this.getItemApi<IShipment>(StorageKeys.SHIPMENTS_KEY);
    }

    get trailers() {
        return this.getItemApi<ITrailer>("trailers");
    }

    get trailerAttachments() {
        return this.getItemApi<ITrailerAttachment>(StorageKeys.TRAILER_ATTACHMENTS_KEY);
    }

    get messages() {
        return this.getItemApi<IMessage>(StorageKeys.MESSAGES_KEY);
    }

    get dvirs() {
        return this.getItemApi<IDVIRLog>(StorageKeys.DVIR_KEY);
    }

    get hosRuleSets() {
        return this.getItemApi<IUserHosRuleSet>(StorageKeys.USER_HOS_RULESETS_KEY);
    }

    get driverChanges() {
        return {
            addItem: (model) => {
                return this.getQueueForSyncType(SyncType.DriverChange).push(resolve => {
                    this.driverChangeSync.addItem(model);
                    resolve();
                });
            },
            getLastItem: (device) => {
                return this.driverChangeSync.getLastDeviceDriverChange(device);
            }
        };
    }

    get trips() {
        return this.getItemApi<Trip>(StorageKeys.USER_TRIPS_KEY);
    }

    get tripAnnotations() {
        return this.getItemApi<TripAnnotation>(StorageKeys.TRIPANNOTATIONS_KEY);
    }

    get groupLinkedDevices() {
        return this.getItemApi(StorageKeys.GROUPLINKEDDEVICES_KEY);
    }

    get misc(): IMiscSync {
        return {
            syncDefectLists: (force = false, device?: Device, trailers?: Trailer[]) => syncDefectLists(this.securityService, this.api, this.store, force, device, trailers)
        };
    }

    get eventLogs() {
        return {
            upload: (continuousUpload: Boolean) => {
                const chunkSize = getUploadChunkSize(constants.LOGGING.SCHEDULED_TASK_UPLOAD_CHUNK_SIZE);
                uploadEventLogsFromIndexedDb(this.api, chunkSize, continuousUpload ? -1 : 1);
            }
        };
    }

    getSyncTypesForStorageKey(type: TSyncApiType): SyncType[] {
        const syncType = this.getSyncTypeFromStorageKey(type);
        if (!syncType) {
            throw new Error(`selectiveSync: no SyncType mapped for storage key "${ type }"`);
        }
        return [syncType];
    }

    addUserHosRuleSet (user: DeepReadonly<IUser>, ruleSet: UserHosRuleSet, userAnnotationString?: string) {
        let getOrSetLog = (latestLog: DutyStatusLog | null, exemptAnnotation: IAnnotation, userAnnotation: IAnnotation | null) => {
            if (latestLog) {
                this.addAnnotationsToTheLog(latestLog, [exemptAnnotation, userAnnotation]);
                return this.dutyStatusLogs.setItem(latestLog, true);
            } else {
                let newLog = this.getNewOffLog();
                this.addAnnotationsToTheLog(newLog, [exemptAnnotation, userAnnotation]);
                return this.dutyStatusLogs.addItem(newLog, true);
            }
        };
        let ruleSetDateTime = ruleSet.dateTime;

        let translator = this.translationService.getTranslator();
        return translator.translate(getRuleSetName(ruleSet.hosRuleSet.id))
            .then(translatedRuleSetName => translator.getString`Ruleset changed to ${ translatedRuleSetName } on ${ formatLocalDate(ruleSetDateTime) }`)
            .then(exemptAnnotationString => {
                // Add new UserHosRuleSet
                // Add or Set the user's latest log
                // Call sync after all above actions are complete
                return this.hosRuleSets.addItem(ruleSet, true)
                    .then(() => {
                        let latestLog = this.dutyStatusLogService.getLatestStatusLog();
                        let userAnnotation = userAnnotationString ? this.hosProcess.getNewAnnotation(userAnnotationString, user, ruleSetDateTime) : null;
                        let exemptAnnotationDateTime = new Date(new Date(ruleSetDateTime).valueOf() - 1);
                        let exemptAnnotation = this.hosProcess.getNewAnnotation(exemptAnnotationString, user, exemptAnnotationDateTime.toISOString());
                        return getOrSetLog(latestLog, exemptAnnotation, userAnnotation);
                    });
            })
            .then(() => this.synchronize(false, null, [SyncType.HOS, SyncType.UserHosRuleSet]))
            .then(() => {
                this.hosProcess.process();
            })
            .catch(handleApi);
    }

    /**
     * Starts a sync operation. Sync operations are queued and run one at a time.
     * @param force Whether to ensure fresh data is fetched from the server and overwrite local data. Only some sync types respect this flag.
     * @param onProgress Optional callback to receive progress updates.
     * @param syncTypes Array of sync types to sync. If null or undefined, a full sync is performed.
     * @param syncSource Optional source of the sync request.
     * @param invertSyncTypes If true, the syncTypes array is treated as a list of sync types NOT to sync.
     * @param onlySyncModifiedData If true, only perform syncs for entities with modified data to be uploaded.
    * @returns A promise that resolves when the sync is complete.
     */
    synchronize(
        force = false,
        onProgress?: ((syncName: string, itemsSynced: number, totalItemSyncs: number,
            error?: Error | IApiError | ITransportError,
            errorDict?: { [syncKey: string]: Error | IApiError | ITransportError }) => void) | null,
        syncTypes?: SyncType[] | null,
        syncSource?: TSyncSource,
        invertSyncTypes = false,
        onlySyncModifiedData = false
    ) {
        const activeGroupSyncTypes = this.getActiveGroupSyncTypes(syncTypes, invertSyncTypes);
        const totalGroups = activeGroupSyncTypes.length;

        if (totalGroups === 0) {
            return Promise.resolve();
        }

        // Split multi-queue syncs into separate synchronizeInternal calls per queue group
        // Each queue handles its own sync types independently, preventing coordination issues
        const syncPromises: Promise<{ [syncKey: string]: Error | IApiError | ITransportError }>[] = [];
        const isMultiQueueSync = totalGroups > 1;

        // For multi-queue syncs, track progress at the group level so onProgress reports
        // accurate totals across all groups (e.g. "2 of 6") instead of per-group counts
        const groupProgress = { synced: 0 };

        for (const [group, groupSyncTypes] of activeGroupSyncTypes) {
            const queue = this.promiseQueues.get(group);
            if (!queue) {
                continue;
            }

            const progressLabel = this.getProgressLabel(group, groupSyncTypes);

            const promise = new Promise<{ [syncKey: string]: Error | IApiError | ITransportError }>(returnErrorDict => {
                queue.push((resolve, resolveWithError) => {
                    // For multi-queue syncs, don't pass onProgress to synchronizeInternal — internal
                    // logging still works, but we report progress at the group level instead.
                    return this.synchronizeInternal(force, groupSyncTypes, isMultiQueueSync ? null : onProgress, syncSource, onlySyncModifiedData, !isMultiQueueSync)
                        .then(errorDict => {
                            if (isMultiQueueSync && onProgress) {
                                groupProgress.synced++;
                                onProgress(progressLabel, groupProgress.synced, totalGroups, undefined, Object.keys(errorDict).length ? errorDict : undefined);
                            }
                            resolve();
                            returnErrorDict(errorDict);
                        })
                        .catch(err => {
                            if (isMultiQueueSync && onProgress) {
                                groupProgress.synced++;
                                onProgress(progressLabel, groupProgress.synced, totalGroups, err);
                            }
                            // synchronizeInternal should never reject, but log via queue's errorLog if it does
                            resolveWithError(err);
                            returnErrorDict({});
                        });
                });
            });
            syncPromises.push(promise);
        }

        if (syncPromises.length === 0) {
            return Promise.resolve();
        }

        return Promise.all(syncPromises).then(errorDicts => {
            if (isMultiQueueSync) {
                // Aggregate all errors from individual syncs and log once
                const aggregatedErrors: { [syncKey: string]: Error | IApiError | ITransportError } = {};
                errorDicts.forEach(dict => {
                    for (const key in dict) {
                        if (Object.prototype.hasOwnProperty.call(dict, key)) {
                            aggregatedErrors[key] = dict[key];
                        }
                    }
                });

                const syncErrorKeys = Object.keys(aggregatedErrors);
                if (syncErrorKeys.length) {
                    const errorMessageParts: string[] = [];
                    syncErrorKeys.forEach(key => errorMessageParts.push(`${ key }:${ aggregatedErrors[key].message }`));
                    logger.error(logger.Source.SYNCERROR, `Syncs that were not completed: [${ errorMessageParts.join(",") }]`, this.api.getUser());
                }

                // Trigger SyncComplete once after all queue syncs have completed
                dispatcher.trigger(DispatcherEvents.SyncComplete);
            }
        });
    }

    waitForSyncsToComplete(timeoutMs: number = 3 * MINUTES) {
        // When this promise resolves, all prior syncs in ALL queues have completed
        const queuePromises: Promise[] = [];

        this.promiseQueues.forEach(queue => {
            if (queue.length === 0) {
                // Queue is empty, nothing to wait for
                return;
            }

            // Create a promise that resolves when this queue completes
            let resolveSyncComplete: () => void;
            const syncCompletePromise = new Promise(resolve => {
                resolveSyncComplete = () => resolve();
            });

            queue.push(resolve => {
                // This task will only execute after all previous tasks in this queue have completed
                resolveSyncComplete();
                resolve();
            });

            queuePromises.push(syncCompletePromise);
        });

        if (queuePromises.length === 0) {
            return Promise.resolve();
        }

        return Promise.all(queuePromises).timeout(timeoutMs);
    }

    onSelectNewVehicle(currentDevice?: IIdEntity | null, previousDevice?: IIdEntity | null): void {
        if (currentDevice && previousDevice && currentDevice.id === previousDevice.id) {
            return;
        }

        removeFeedVersion(this.api, this.store, FeedTypeKey.DVIR_VEHICLE);
        removeFeedVersion(this.api, this.store, FeedTypeKey.DEVICE_TEXT_MESSAGE);
        removeFeedVersion(this.api, this.store, FeedTypeKey.TRAILER_ATTACHMENT);
        removeFeedVersion(this.api, this.store, FeedTypeKey.SHIPMENT);
        this.trailerAttachmentSync.invalidateTrailerCache();
    }

    destroy() {
    }

    /**
     * Returns each affected queue group paired with the sync types that should run within it.
     * Groups with no applicable types (after filtering by syncTypes/invertSyncTypes) are omitted.
     * @param syncTypes Array of sync types. If null/undefined/empty, all groups and types are included.
     * @param invertSyncTypes If true, syncTypes is treated as an exclusion list.
     * @returns Array of [SyncQueueGroup, SyncType[]] pairs for each group with at least one applicable type.
     */
    private getActiveGroupSyncTypes(syncTypes: SyncType[] | null | undefined, invertSyncTypes: boolean): [SyncQueueGroup, SyncType[]][] {
        return syncGroups.reduce((acc, group) => {
            const groupSyncTypes = this.getSyncTypesForGroup(group, syncTypes, invertSyncTypes);
            if (groupSyncTypes.length > 0) {
                acc.push([group, groupSyncTypes]);
            }
            return acc;
        }, [] as [SyncQueueGroup, SyncType[]][]);
    }

    private getSyncTypesForGroup(group: SyncQueueGroup, requestedSyncTypes: SyncType[] | null | undefined, invertSyncTypes: boolean): SyncType[] {
        const allGroupSyncTypes = QUEUE_GROUP_TO_SYNC_TYPES[group];

        if (!requestedSyncTypes || requestedSyncTypes.length === 0) {
            return allGroupSyncTypes;
        }

        if (invertSyncTypes) {
            return allGroupSyncTypes.filter(st => requestedSyncTypes.indexOf(st) === -1);
        }

        return requestedSyncTypes.filter(st => allGroupSyncTypes.indexOf(st) !== -1);
    }

    /**
     * Returns a human-readable label for the given queue group, used in onProgress messages
     * (e.g. "Synchronized HOS logs (1 of 6)..."). If the group contains only a single
     * non-primary sync type, that specific type name is returned instead.
     */
    private getProgressLabel(group: SyncQueueGroup, groupSyncTypes: SyncType[]): string {
        if (groupSyncTypes.length === 1) {
            switch (groupSyncTypes[0]) {
                case SyncType.UserHosRuleSet: return "UserHosRuleSet";
                case SyncType.TrailerAttachments: return "TrailerAttachment";
                default: break;
            }
        }

        switch (group) {
            case SyncQueueGroup.HOS: return "HOS";
            case SyncQueueGroup.DVIR: return "DVIR";
            case SyncQueueGroup.SHIPMENTS: return "Shipment";
            case SyncQueueGroup.MESSAGES: return "Messages";
            case SyncQueueGroup.DRIVER_CHANGE: return "DriverChange";
            case SyncQueueGroup.TRIP: return "Trip";
            default: return group;
        }
    }

    private getQueueForSyncType(syncType: SyncType): PromiseQueue {
        const queueGroup = SYNC_TYPE_TO_QUEUE_GROUP[syncType];
        const queue = this.promiseQueues.get(queueGroup);

        if (!queue) {
            throw new Error(`No queue found for sync type: ${ syncType }`);
        }

        return queue;
    }

    private getSyncTypeFromStorageKey(storageKey: TSyncApiType): SyncType {
        const keyToType: Record<TSyncApiType, SyncType> = {
            [StorageKeys.USER_LOGS_KEY]: SyncType.HOS,
            // SYSTEM_LOGS is not usually used outside of HOS sync, but it is here just in case
            [StorageKeys.SYSTEM_LOGS_KEY]: SyncType.HOS,
            [StorageKeys.HOSANNOTATIONS_KEY]: SyncType.HOS,
            [StorageKeys.USER_HOS_RULESETS_KEY]: SyncType.UserHosRuleSet,
            [StorageKeys.DVIR_KEY]: SyncType.DVIR,
            [StorageKeys.TRAILER_ATTACHMENTS_KEY]: SyncType.TrailerAttachments,
            "trailers": SyncType.TrailerAttachments,
            [StorageKeys.SHIPMENTS_KEY]: SyncType.Shipments,
            [StorageKeys.MESSAGES_KEY]: SyncType.Messages,
            [StorageKeys.DRIVER_CHANGES_KEY]: SyncType.DriverChange,
            [StorageKeys.USER_TRIPS_KEY]: SyncType.Trip,
            [StorageKeys.TRIPANNOTATIONS_KEY]: SyncType.TripAnnotation,
            [StorageKeys.GROUPLINKEDDEVICES_KEY]: SyncType.GroupLinkedDevice
        };

        return keyToType[storageKey];
    }

    private getQueueForStorageKey(storageKey: TSyncApiType): PromiseQueue {
        const syncType = this.getSyncTypeFromStorageKey(storageKey);
        return this.getQueueForSyncType(syncType);
    }

    private addAnnotationsToTheLog (log: DutyStatusLog, annotations: (IAnnotation | null)[]) {
        annotations.forEach(a => a && log.addAnnotation(a));
    }

    private getNewOffLog() {
        let user = this.userService.getCurrentUser();
        let device = getCurrentVehicle();

        return new DutyStatusLog({
            id: idSerializer.get(),
            dateTime: getServerDate().toISOString(),
            status: "OFF",
            device: { id: device ? device.id : "NoDeviceId" },
            driver: { id: user ? user.id : "NoUserId" },
            origin: "Manual",
            annotations: []
        });
    }

    private logErrorAndThrowException (logText: string, error: Error, apiUser) {
        logger.error(logger.Source.SYNC, `Improper log change: ${ logText }`, apiUser);
        throw error;
    }

    // eslint-disable-next-line complexity
    private getStorage<T> (storageKey: string, overrideKey?: IIdEntity) {
        let keyHolder = overrideKey ?? this.api.getUser();

        switch (storageKey) {
            case StorageKeys.HOSANNOTATIONS_KEY:
                return keyHolder ? extend(true, {}, { ...this.store.getState().hosAnnotations[keyHolder.id] }) : null;
            case StorageKeys.USER_LOGS_KEY:
                if (!keyHolder){
                    return null;
                }
                return extend(true, {}, {...this.store.getState().userHosLogs[keyHolder.id]});
            case StorageKeys.SYSTEM_LOGS_KEY:
                if (!keyHolder){
                   return null;
                }
                return extend(true, {}, {...this.store.getState().systemHosLogs[keyHolder.id]});
            case StorageKeys.USER_HOS_RULESETS_KEY:
                if (!keyHolder){
                    return null;
                }
                return extend(true, {}, {...this.store.getState().userHosRuleSets[keyHolder.id]});
            case StorageKeys.DVIR_KEY:
                if (!keyHolder){
                    return null;
                }
                return extend(true, {}, {...this.store.getState().dvirLogs[keyHolder.id]});
            case StorageKeys.USER_TRIPS_KEY:
                if (!keyHolder){
                    return null;
                }
                return extend(true, {}, {...this.store.getState().trips[keyHolder.id]});
            case StorageKeys.TRIPANNOTATIONS_KEY:
                if (!keyHolder) {
                    return null;
                }
                return extend(true, {}, {...this.store.getState().tripAnnotations[keyHolder.id]});
            case StorageKeys.GROUPLINKEDDEVICES_KEY:
                if (!keyHolder) {
                    return null;
                }
                return extend(true, {}, {...this.store.getState().groupLinkedDevices[keyHolder.id]});
            case StorageKeys.MESSAGES_KEY:
                if (!keyHolder) {
                    return null;
                }
                return extend(true, {}, {...this.store.getState().messages[keyHolder.id]});
            case StorageKeys.DRIVER_CHANGES_KEY:
                if (!keyHolder) {
                    return null;
                }
                return extend(true, [], [...(this.store.getState().driverChanges[keyHolder.id] ?? [])]);
            case StorageKeys.TRAILER_ATTACHMENTS_KEY:
                if (!keyHolder) {
                    return null;
                }
                return extend(true, {}, {...this.store.getState().trailerAttachments[keyHolder.id]});
            case StorageKeys.SHIPMENTS_KEY:
                if (!keyHolder) {
                    return null;
                }
                return extend(true, {}, {...this.store.getState().shipments[keyHolder.id]});
            default:
                return storage.get<T>(storageKey, true, keyHolder ? keyHolder.id : undefined);
        }
    }

    // eslint-disable-next-line complexity
    private setStorage (storageKey, currentStorage, overrideKey?: IIdEntity, currentItem?) {
        let keyHolder = overrideKey ?? this.api.getUser();
        switch (storageKey) {
            case StorageKeys.HOSANNOTATIONS_KEY:
                if (!keyHolder) {
                    return;
                }
                this.store.dispatch(HosAnnotationActions.setAnnotations({ userId: keyHolder.id, annotations: currentStorage }));
                return;
            case StorageKeys.USER_LOGS_KEY:
                if (!keyHolder){
                    return;
                }
                this.store.dispatch(HosLogActions.setUserHosLogs({ userId: keyHolder.id, logs: currentStorage }));
                return;
            case StorageKeys.SYSTEM_LOGS_KEY:
                if (!keyHolder){
                    return;
                }
                this.store.dispatch(HosLogActions.setSystemHosLogs({ userId: keyHolder.id, logs: currentStorage }));
                return;
            case StorageKeys.USER_HOS_RULESETS_KEY:
                if (!keyHolder){
                    return;
                }
                this.store.dispatch(UserHosRuleSetActions.setUserHosRuleSets({ userId: keyHolder.id, userHosRuleSets: currentStorage }));
                return;
            case StorageKeys.DVIR_KEY:
                if (!keyHolder) {
                    return;
                }
                this.store.dispatch(DvirLogActions.setDvirLogs({userId: keyHolder.id, logs: currentStorage}));
                return;
            case StorageKeys.USER_TRIPS_KEY: {
                if (!keyHolder){
                    return;
                }
                const timeZoneId = this.store.getState().timeZoneInfo[keyHolder.id]?.timeZoneId;
                this.store.dispatch(TripActions.setTrips({
                    userId: keyHolder.id,
                    trips: currentStorage,
                    startOfDay: getTripStartOfDay(timeZoneId)
                }));
                return;
            }
            case StorageKeys.TRIPANNOTATIONS_KEY:
                if (!keyHolder){
                    return;
                }
                this.store.dispatch(TripAnnotationActions.setTripAnnotations({ deviceId: keyHolder.id, tripAnnotations: currentStorage }));
                return;
            case StorageKeys.GROUPLINKEDDEVICES_KEY:
                if (!keyHolder){
                    return;
                }
                this.store.dispatch(GroupLinkedDeviceActions.setGroupLinkedDevices({ deviceId: keyHolder.id, groupLinkedDevices: currentStorage }));
                return;
            case StorageKeys.MESSAGES_KEY:
                if (!keyHolder){
                    return;
                }
                this.store.dispatch(MessageActions.setMessages({ userId: keyHolder.id, messages: currentStorage }));
                return;
            case StorageKeys.DRIVER_CHANGES_KEY:
                if (!keyHolder) {
                    return;
                }
                this.store.dispatch(DriverChangesActions.setDriverChanges({userId: keyHolder.id, driverChanges: currentStorage}));
                return;
            case StorageKeys.TRAILER_ATTACHMENTS_KEY:
                if (!keyHolder){
                    return;
                }
                if (currentItem) {
                    let currentUser = this.userService.getUserById(keyHolder.id);
                    let codrivers = this.userService.getCoDrivers(currentUser?.name);
                    codrivers.forEach(codriver => {
                        let attachments = extend(true, {}, this.store.getState().trailerAttachments[codriver.id] || {}) as IIdDictionary<ITrailerAttachment>;
                        attachments[currentItem.id] = currentItem;
                        this.store.dispatch(TrailerAttachmentActions.setTrailerAttachments({ userId: codriver.id, trailerAttachments: attachments }));
                    });
                }
                this.store.dispatch(TrailerAttachmentActions.setTrailerAttachments({ userId: keyHolder.id, trailerAttachments: currentStorage }));
                return;
            case StorageKeys.SHIPMENTS_KEY: {
                if (!keyHolder) {
                    return;
                }
                this.store.dispatch(ShipmentActions.setShipments({ keyId: keyHolder.id, shipments: currentStorage }));
                return;
            }
            default:
                logger.error(logger.Source.SYNC, `Unknown storage key: ${ storageKey }`);
                throw new Error(`Unknown storage key: ${ storageKey }`);
        }
    }
    private rebuildLocalModel(storageKey: string, model: any) {
        if (!model.id) {
            return model;
        }

        let items;

        if (model instanceof TripAnnotation) {
            items = this.getStorage<any>(storageKey, model.device) || {};
        } else if (model instanceof GroupLinkedDevice) {
            items = this.getStorage<any>(storageKey, model.linkedEntity) || {};
        } else if (model instanceof Shipment) {
            const deviceId = this.getShipmentDeviceId(model);
            if (deviceId) {
                items = this.getStorage(storageKey, {id: deviceId});
            }
        } else {
            items = this.getStorage<any>(storageKey) || {};
        }

        let updatedModel = items[model.id];

        return updatedModel;
    }

    private removeItem<T extends IHashMap<T>> (syncType: TSyncApiType, model: T, syncLater = false) {
        let storageKey: TSyncApiType = syncType;
        const removeItemInternalFunc = resolve => {
            if (!storageKey || !model) {
                return resolve();
            }

            let currentStorage = this.getStorage<any>(storageKey) || {};
            let modelInStorage = currentStorage[model.id];

            // If model is already added to localStorage, but is being modified, just set the object with the new properties, leaving the previous modified state intact
            if (model.id) {
                if (!modelInStorage) {
                    logger.log(logger.Source.SYNC, `Set model (${ storageKey }) with id (${ model.id }) which was not found in localStorage`);
                } else {
                    model.previousState = modelInStorage.modified && modelInStorage.previousState || extend({}, modelInStorage);
                }
            }

            // Remove takes priority over all other modified props
            model.modified = "Remove";
            currentStorage[model.id] = model;

            logger.log(logger.Source.DEBUG, `${ syncType } removeItem: ${ JSON.stringify(model) } syncLater: ${ syncLater }`);
            this.setStorage(storageKey, currentStorage);

            if (!syncLater) {
                this.synchronizeInternal(false, this.getSyncTypesForStorageKey(syncType)).then(() => resolve(this.rebuildLocalModel(storageKey, model)));
            } else {
                dispatcher.trigger(DispatcherEvents.SyncComplete);
                resolve(model);
            }
        };

        if (model instanceof DutyStatusLog && storageKey === StorageKeys.USER_LOGS_KEY) {
            // Reset storageKey based on HOS log status
            storageKey = model.isRegularDisplayLog() ? StorageKeys.USER_LOGS_KEY : StorageKeys.SYSTEM_LOGS_KEY;
        }

        return this.getQueueForStorageKey(storageKey).push(removeItemInternalFunc);
    }

    private setItem<T extends IHashMap<T>> (syncType: TSyncApiType, model: Backbone.Model | T, syncLater = false, setModified = true) {
        let storageKey: TSyncApiType = syncType;
        let setItemInternalFunc = resolve => {
            if (!storageKey || !model) {
                return resolve();
            }

            let currentStorage;

            if (model instanceof TripAnnotation) {
                currentStorage = this.getStorage<any>(storageKey, model.device);
            } else if (model instanceof GroupLinkedDevice) {
                currentStorage = this.getStorage<any>(storageKey, model.linkedEntity);
            } else if (model instanceof Shipment) {
                const deviceId = this.getShipmentDeviceId(model);
                if (deviceId) {
                    currentStorage = this.getStorage(storageKey, {id: deviceId});
                }
            } else {
                currentStorage = this.getStorage<any>(storageKey) || {};
            }

            let modelInStorage = currentStorage[model.id];

            let modelObj = <T>model;

            if (model instanceof Backbone.Model) {
                modelObj = model.toJSON() as T;
            }

            // If model is already added to localStorage, but is being modified, just set the object with the new
            // properties, leaving the previous modified state intact
            if (modelObj.id && modelInStorage && modelInStorage.modified) {
                // Add modified of the model in storage, in case it is not there
                if (setModified) {
                    modelObj.modified = modelInStorage.modified;
                }
                modelObj.previousState = modelInStorage.previousState;
                currentStorage[modelObj.id] = modelObj;
            } else {
                // If model already has an id, then update the localStorage entry with that id
                if (modelInStorage) {
                    modelObj.previousState = extend({}, modelInStorage);
                }
                logger.log(logger.Source.SYNC, `Set model (${ storageKey }) with id (${ modelObj.id }) which was not found in localStorage`);

                if (setModified) {
                    modelObj.modified = "Set";
                }

                currentStorage[modelObj.id] = modelObj;
            }

            logger.log(logger.Source.DEBUG, `${ syncType } setItem: ${ JSON.stringify(modelObj) } syncLater: ${ syncLater } setModified: ${ setModified }`);

            if (model instanceof TripAnnotation) {
                this.setStorage(storageKey, currentStorage, model.device);
            } else if (model instanceof GroupLinkedDevice) {
                this.setStorage(storageKey, currentStorage, model.linkedEntity);
            } else if (model instanceof TrailerAttachment) {
                this.setStorage(storageKey, currentStorage, undefined, modelObj);
            } else if (model instanceof Shipment) {
                const deviceId = this.getShipmentDeviceId(model);
                deviceId && this.setStorage(storageKey, currentStorage, {id: deviceId});
            } else {
                this.setStorage(storageKey, currentStorage);
            }

            if (!syncLater) {
                return this.synchronizeInternal(false, this.getSyncTypesForStorageKey(syncType)).then(() => {
                    resolve(this.rebuildLocalModel(storageKey, modelObj));
                });
            } else {
                dispatcher.trigger(DispatcherEvents.SyncComplete);
                resolve(modelObj);
            }
        };


        if (storageKey === StorageKeys.USER_LOGS_KEY && model instanceof DutyStatusLog) {
            //TODO: Move this section into the generic `checkValue` method that should be called for all sync entities
            //      (MYG-148762 covers trailer attachments in the pre-queue checkValue branch below)
            let hosLogModel = new DutyStatusLog(model);
            let apiUser = this.api.getUser();

            if (!hosLogModel.getDate()) {
                this.logErrorAndThrowException(JSON.stringify(hosLogModel), new Error("Log should have date"), apiUser);
            }

            // Reset storageKey based on HOS log status
            storageKey = hosLogModel.isRegularDisplayLog() ? StorageKeys.USER_LOGS_KEY : StorageKeys.SYSTEM_LOGS_KEY;
        }

        return this.getQueueForStorageKey(storageKey).push(setItemInternalFunc);
    }

    private addItem<T extends IHashMap<T>>(syncType: TSyncApiType, model: Backbone.Model | T, syncLater = false, skipValidation = false) {
        let storageKey: TSyncApiType = syncType;
        let addItemFunc = resolve => {
            let currentStorage;
            if (model instanceof TripAnnotation) {
                currentStorage = this.getStorage<any>(storageKey, model.device);
            } else if (model instanceof GroupLinkedDevice) {
                currentStorage = this.getStorage<any>(storageKey, model.linkedEntity);
            } else if (model instanceof Shipment) {
                const deviceId = this.getShipmentDeviceId(model);
                if (deviceId) {
                    currentStorage = this.getStorage<any>(storageKey, { id: deviceId });
                }
            } else {
                currentStorage = this.getStorage<any>(storageKey) || {};
            }
            let modelObj = <T>model;

            if (model instanceof Backbone.Model) {
                modelObj = model.toJSON() as T;
            }

            // If new model, generate a GUID and add to localStorage
            if (!modelObj.id) {
                modelObj.id = idSerializer.get();
            }

            modelObj.previousState = {} as T;
            modelObj.modified = "Add";
            currentStorage[modelObj.id] = modelObj;

            logger.log(logger.Source.DEBUG, `${ syncType } addItem: ${ JSON.stringify(modelObj) } syncLater: ${ syncLater }`);

            if (model instanceof TripAnnotation) {
                this.setStorage(storageKey, currentStorage, model.device);
            } else if (model instanceof GroupLinkedDevice) {
                this.setStorage(storageKey, currentStorage, model.linkedEntity);
            } else if (model instanceof TrailerAttachment) {
                this.setStorage(storageKey, currentStorage, undefined, modelObj);
            } else if (model instanceof Shipment) {
                const deviceId = this.getShipmentDeviceId(model);
                if (deviceId) {
                    this.setStorage(storageKey, currentStorage, { id: deviceId });
                }
            } else {
                this.setStorage(storageKey, currentStorage);
            }

            // JS Interface for Java/iOS applications 11
            let clone: {} & T | undefined = undefined;
            if (storageKey === this.hosSync.storageKey || storageKey === this.hosSync.storageSystemKey) {
                clone = extend(true, {}, modelObj);
                delete clone.previousState;
                delete clone.modified;

                // GeotabJSInterface is a deprecated object+method for Pegasus. Remains until `webViewLayer` is released + implemented.
                if (window.GeotabJSInterface && window.GeotabJSInterface.AddHosManual) {
                    try {
                        window.GeotabJSInterface.AddHosManual(JSON.stringify(clone));
                    } catch {
                        try {
                            window.GeotabJSInterface.AddHosManual();
                        } catch (e: any) {
                            logger.error(logger.Source.SYNC, `GeotabJSInterface.AddHosManual ${ e.message }`);
                        }
                    }
                }

                dispatcher.trigger(DispatcherEvents.WarnOmittedDataOnDSL, clone);
            }

            if (!syncLater) {
                return this.synchronizeInternal(false, this.getSyncTypesForStorageKey(syncType)).then(() => {
                    clone !== undefined && dispatcher.trigger(DispatcherEvents.WebViewLayerNewDutyStatusLog, clone);

                    resolve(this.rebuildLocalModel(storageKey, modelObj));
                });
            } else {
                dispatcher.trigger(DispatcherEvents.SyncComplete);
                clone !== undefined && dispatcher.trigger(DispatcherEvents.WebViewLayerNewDutyStatusLog, clone);
                return resolve(modelObj);
            }
        };

        if (storageKey === StorageKeys.USER_LOGS_KEY && model instanceof DutyStatusLog) {
            if (this.featureFlagsService.hasFeatureFlag(FeatureFlagName.ENABLE_DSL_SPAM_DETECTOR)) {
                dslSpamDetector.record(model, { source: "addItem", syncLater });
            }
            //TODO: Move this section into the generic `checkValue` method that should be called for all sync entities
            //      (MYG-148762 covers trailer attachments in the pre-queue checkValue branch below)
            let hosLogModel = new DutyStatusLog(model);
            let apiUser = this.api.getUser();

            if (!hosLogModel.getDate()) {
                this.logErrorAndThrowException(JSON.stringify(hosLogModel), new Error("Log should have date"), apiUser);
            }

            return (skipValidation ? Promise.resolve([]) : this.hosProcess.validateHosLog(hosLogModel))
                .then(this.showWarningMessage.bind(this))
                .then(() => {
                    // Reset the key depending on the HOS status
                    storageKey = hosLogModel.isRegularDisplayLog() ? StorageKeys.USER_LOGS_KEY : StorageKeys.SYSTEM_LOGS_KEY;
                })
                .then(() => this.getQueueForStorageKey(storageKey).push(addItemFunc));
        }

        if (storageKey === StorageKeys.TRAILER_ATTACHMENTS_KEY && !skipValidation) {
            // MYG-148762: trailer attachments were never validated on addItem, so an attachment with no
            // trailer.id was written to local storage and pushed to the server as a null-trailer
            // TrailerAttachment. Validate before queuing so it rejects this promise instead, with nothing
            // stored. Not feature-flagged: see "Part A" in the MR description.
            // A step towards the TODO above rather than away from it: the generic version of this is to run
            // it for every storage key whose sub-sync implements checkValue, which needs each sub-sync's
            // checkValue audited for pre-queue side effects first.
            return this.checkValue(storageKey, model).then(() => this.getQueueForStorageKey(storageKey).push(addItemFunc));
        }

        return this.getQueueForStorageKey(storageKey).push(addItemFunc);
    }

    private checkValue<T>(type: TSyncApiType, model: Backbone.Model | IHashMap<T>) {
        return new Promise(resolve => {
            let modelObj: {} = model;

            if (model instanceof Backbone.Model) {
                modelObj = model.toJSON();
            }

            // These checkValues are synchronous but will `throw` handled exceptions if invalid
            switch (type) {
                case StorageKeys.USER_LOGS_KEY:
                    this.hosSync.checkValue(modelObj as DutyStatusLog);
                    break;
                case StorageKeys.TRAILER_ATTACHMENTS_KEY:
                    this.trailerAttachmentSync.checkValue(modelObj as ITrailerAttachment);
                    break;
                case StorageKeys.SHIPMENTS_KEY:
                    this.shipmentSync.checkValue(modelObj as IShipment);
                    break;
                case StorageKeys.MESSAGES_KEY:
                    this.messagesSync.checkValue(modelObj as IMessage);
                    break;
                default:
                    break;
            }
            resolve();
        });
    }

    private getShipmentDeviceId(model: Shipment) {
        return typeof model.device === "object" ? model.device.id : model.device;
    }

    private showWarningMessage (validationResults?: TViolationValidationResult) {
        return new Promise((res, rej) => {
            if (!validationResults || !validationResults.length) {
                res();
            } else {
                this.hosProcess.showStatusChangeWarning(validationResults, res, rej);
            }
        });
    }

    private getItemApi<T extends IHashMap<T>>(type: TSyncApiType) {
        return {
            addItem: (model: Backbone.Model | T, syncLater = false, skipValidation = false): Promise => this.addItem<T>(type, model, syncLater, skipValidation),
            setItem: (model: Backbone.Model | T, syncLater = false, setModified = true): Promise => this.setItem<T>(type, model, syncLater, setModified),
            checkValue: (model: Backbone.Model | T): Promise => this.checkValue(type, model)
        };
    }

     private getItemApiWithRemove<T extends IHashMap<T>>(type: TSyncApiType) {
        return {
            ...this.getItemApi(type),
            removeItem: (model: T, syncLater = false): Promise => this.removeItem<T>(type, model, syncLater)
        };
    }

    private isNoVehicleSync<T>(storageKey: StorageKeys) {
        let localLogs = this.getStorage<T>(storageKey);
        let currentVehicle = getCurrentVehicle();
        return currentVehicle && currentVehicle.id === NODEVICEID ? Object.keys(localLogs || {}).length : false;
    }

    private syncDvirAndTrailers(
        apiUserId: string,
        syncMethods: (() => Promise)[],
        needsSync: (type: SyncType) => boolean,
        hasVehicle: boolean,
        profilingBatcher: PerformanceProfileBatcher,
        progressCb: TSyncCallback,
        force = false
    ) {
        let syncCount = 0;

        let canSyncDvir = this.securityService.can("viewDvir", apiUserId) && (hasVehicle || this.isNoVehicleSync<IDVIRLog>(StorageKeys.DVIR_KEY));
        let canSyncMedia = this.securityService.can("viewMedia", apiUserId);

        let syncMediaImpl = () => this.mediaSync.synchronize(profilingBatcher).then(([type, errorObject]) => progressCb(type, errorObject));

        // A defect photo can only be attached to its inspection on the pass that pushes the
        // inspection: dvir.ts writes the media reference into the defect remark while the log still
        // carries `modified`, and a successful push clears that flag for good. A photo whose bytes
        // are not on the server yet is skipped by that step, so pushing DVIR first spends the only
        // window there is and the photo is orphaned — it uploads moments later, referenced by
        // nothing (MYG-144154).
        //
        // So when the queue holds undelivered media, the bytes go up first. This is the ordering
        // inspect.ts already uses when a driver signs (a Media-only sync, then store the DVIR); it
        // only breaks down when that sync was offline. With nothing pending the order is unchanged.
        const uploadPendingMediaFirst = canSyncMedia && hasMediaPendingDelivery(this.store.getState().data.images);

        // Outbound only. The post-DVIR pass below is the one that downloads media newly referenced
        // by server logs, so repeating that work here would double it on every affected sync.
        let uploadPendingMediaImpl = () => this.mediaSync.uploadPending(profilingBatcher).then(([type, errorObject]) => progressCb(type, errorObject));

        let syncDvirImpl = () => {
            return (uploadPendingMediaFirst ? uploadPendingMediaImpl() : Promise.resolve())
                .then(() => this.dvirSync.synchronize(profilingBatcher))
                .then(([type, errorObject]) => {
                    progressCb(type, errorObject);
                    // Still runs afterwards: fetchLogs registers media newly referenced by logs
                    // pulled from the server, and those have to be downloaded.
                    return (canSyncMedia ? syncMediaImpl() : Promise.resolve());
                });
        };

        if (this.securityService.can("viewTrailers", apiUserId)
            && needsSync(SyncType.TrailerAttachments) && (hasVehicle || this.isNoVehicleSync<ITrailerAttachment>(StorageKeys.TRAILER_ATTACHMENTS_KEY))) {
            canSyncDvir && syncCount++;
            canSyncDvir && canSyncMedia && syncCount++;
            canSyncDvir && uploadPendingMediaFirst && syncCount++;

            syncMethods.push(() => {
                return this.trailerAttachmentSync.synchronize(profilingBatcher, force)
                    .then(([type, errorObject]) => {
                        progressCb(type, errorObject);
                        return (canSyncDvir ? syncDvirImpl() : Promise.resolve());
                    });
            });
        } else if (canSyncDvir && needsSync(SyncType.DVIR)) {
            canSyncMedia && syncCount++;
            uploadPendingMediaFirst && syncCount++;
            syncMethods.push(() => syncDvirImpl());
        } else if (canSyncMedia && needsSync(SyncType.Media)) {
            syncMethods.push(() => syncMediaImpl());
        }
        return syncCount;
    }

    private syncHos(
        apiUserId: string,
        syncMethods: (() => Promise)[],
        needsSync: (type: SyncType) => boolean,
        profilingBatcher: PerformanceProfileBatcher,
        progressCb: TSyncCallback,
        force: boolean,
        syncSource?: TSyncSource
    ): number {
        if (!this.securityService.can("attemptHos", apiUserId)) {
            return 0;
        }

        const syncHosImpl = (shouldHosForceSync) => {
            return this.hosSync.synchronize((type, errorObject, syncLogger) => {
                if (isVehicleRemoved(errorObject as IApiError)) {
                    dispatcher.trigger(DispatcherEvents.VehicleRemoved);
                }
                progressCb(type, errorObject, syncLogger);
            }, shouldHosForceSync, syncSource, profilingBatcher);
        };
        const shouldSyncHos = needsSync(SyncType.HOS);
        const shouldSyncUserHosRuleSet = needsSync(SyncType.UserHosRuleSet);

        if (shouldSyncUserHosRuleSet) {
            const combinedSyncImpl = () => {
                return this.userHosRuleSetSync.synchronize(progressCb, profilingBatcher)
                    .then(shouldHosForceSync => {
                        // shouldHosForceSync will be true if the retention period changed due to a ruleset change. In that case, we want to sync hos immediately.
                        if (shouldSyncHos || shouldHosForceSync) {
                            return syncHosImpl(shouldHosForceSync || force);
                        }
                        return Promise.resolve();
                    });
            };
            syncMethods.push(combinedSyncImpl);
            return 1;
        }

        if (shouldSyncHos) {
            syncMethods.push(() => syncHosImpl(force));
        }
        return 0;
    }

    private syncTrips(
        apiUserId: string,
        syncMethods: (() => Promise)[],
        needsSync: (type: SyncType) => boolean,
        profilingBatcher: PerformanceProfileBatcher,
        progressCb: TSyncCallback
    ) {
        let syncCount = 0;
        let canSyncTrips = this.securityService.can("showTrips", apiUserId);
        let needsSyncTrip = needsSync(SyncType.Trip);
        let shouldSyncAnnotation = this.securityService.can("viewTripAnnotation", apiUserId) && needsSync(SyncType.TripAnnotation);
        let shouldSyncGroupDevice = this.securityService.can("manageGroupDevice") && needsSync(SyncType.GroupLinkedDevice);

        if (canSyncTrips) {
            if (needsSyncTrip) {
                shouldSyncAnnotation && syncCount++;
                shouldSyncGroupDevice && syncCount++;
                syncMethods.push(() => this.tripSync.synchronize(progressCb, profilingBatcher)
                    .then(syncInterval => {
                        let updatedSyncInterval: ISyncInterval = { fromDate: syncInterval.extendedFromDate || syncInterval.fromDate, toDate: syncInterval.toDate };
                        let additionalSyncs: Promise<void>[] = [
                            (shouldSyncAnnotation
                                ? this.tripAnnotationSync.synchronize(progressCb, updatedSyncInterval, profilingBatcher)
                                : Promise.resolve()),
                            (shouldSyncGroupDevice
                                ? this.groupLinkedDeviceSync.synchronize(progressCb, updatedSyncInterval, profilingBatcher)
                                : Promise.resolve())
                        ];
                        return Promise.all(additionalSyncs)
                            .then(() => dispatcher.trigger(DispatcherEvents.SyncTrips, syncInterval.fromDate));
                    }));
            } else {
                shouldSyncAnnotation && syncMethods.push(() => this.tripAnnotationSync.synchronize(progressCb, undefined, profilingBatcher));
                shouldSyncGroupDevice && syncMethods.push(() => this.groupLinkedDeviceSync.synchronize(progressCb, undefined, profilingBatcher));
            }
        }
        return syncCount;
    }

    private getStoreForSyncType(syncType: SyncType, overrideKey?: IIdEntity) {
        const keyHolder = overrideKey ?? this.api.getUser();
        if (!keyHolder) {
            throw new Error("No key holder available for getStoreForSyncType");
        }
        switch (syncType) {
            case SyncType.HOS:
                return {
                    ...this.store.getState().userHosLogs[keyHolder.id],
                    ...this.store.getState().systemHosLogs[keyHolder.id],
                    ...this.store.getState().hosAnnotations[keyHolder.id]
                };
            case SyncType.DVIR:
                return this.store.getState().dvirLogs[keyHolder.id];
            case SyncType.Shipments:
                return this.store.getState().shipments[keyHolder.id];
            case SyncType.TrailerAttachments:
                return this.store.getState().trailerAttachments[keyHolder.id];
            case SyncType.Messages:
                return this.store.getState().messages[keyHolder.id];
            case SyncType.UserHosRuleSet:
                return this.store.getState().userHosRuleSets[keyHolder.id];
            case SyncType.DriverChange:
                return this.store.getState().driverChanges[keyHolder.id];
            case SyncType.Trip:
                return this.store.getState().trips[keyHolder.id];
            case SyncType.TripAnnotation:
                return this.store.getState().tripAnnotations[keyHolder.id];
            case SyncType.GroupLinkedDevice:
                return this.store.getState().groupLinkedDevices[keyHolder.id];
            default:
                throw new Error(`Unsupported syncType in getStoreForSyncType: ${ syncType }`);
        }
    }

    private hasModifiedEntitiesInStorage(syncType: SyncType, device?: IIdEntity): boolean {
        const currentState = this.getStoreForSyncType(syncType, device);
        for (const id in currentState) {
            if (Object.prototype.hasOwnProperty.call(currentState, id)) {
                const item = currentState[id];
                if (item && item.modified) {
                    return true;
                }
            }
        }
        return false;
    }

    private hasModifiedEntitiesToSync(syncType: SyncType): boolean {
        if (syncType === SyncType.DriverChange) {
            // Always sync driver changes because they do not use the "modified" property
            return true;
        }

        const currentVehicle = getCurrentVehicle();

        if (syncType === SyncType.Shipments) {
            // Shipments are stored by device, however they could also associated with a driver
            // Return true if there is no current vehicle to ensure shipments are synced if they are still only associated with a driver
            return !currentVehicle || this.hasModifiedEntitiesInStorage(syncType, currentVehicle);
        }

        if (syncType === SyncType.TripAnnotation || syncType === SyncType.GroupLinkedDevice) {
            return !!currentVehicle && this.hasModifiedEntitiesInStorage(syncType, currentVehicle);
        }

        try {
            return this.hasModifiedEntitiesInStorage(syncType);
        } catch (e) {
            // Sync types without modified properties (e.g., Media) should always sync
            return true;
        }
    }

    private hasPendingCoachingWork(userId: string): boolean {
        const coachingSlice = this.store.getState().coaching?.[userId];
        if (!coachingSlice) {
            return false;
        }

        // Check for any pending comments
        const comments = coachingSlice.comments;
        if (comments) {
            const hasPendingComments = Object.keys(comments).some(sessionId =>
                comments[sessionId].some(comment => comment.modified === "Add" && comment.syncStatus === "pending"));
            if (hasPendingComments) {
                return true;
            }
        }

        // Check for any pending completions
        const completions = coachingSlice.pendingCompletions;
        if (completions) {
            const hasPendingCompletions = Object.keys(completions).some(key =>
                completions[key].modified === "Add" && completions[key].syncStatus === "pending");
            if (hasPendingCompletions) {
                return true;
            }
        }

        return false;
    }

    private synchronizeInternal(
        force = false,
        syncTypes: SyncType[],
        onProgress?: ((syncName: string, itemsSynced: number, totalItemSyncs: number,
            error?: Error | IApiError | ITransportError,
            errorDict?: { [syncKey: string]: Error | IApiError | ITransportError }) => void) | null,
        syncSource?: TSyncSource,
        onlySyncModifiedData = false,
        handleSyncCompletion = true
    ): Promise<{ [syncKey: string]: Error | IApiError | ITransportError }> {
        return new Promise(resolve => {
            let apiUser = this.api.getUser();
            let apiUserId = apiUser ? apiUser.id : undefined;
            let hasVehicle = !isNoVehicle();

            // An array of methods that return a promise. Prevents promises from firing on declaration
            let syncMethods: (() => Promise)[] = [];

            let syncCount = 0;

            let itemsSynced = 0;
            const needsSync = (syncType: SyncType) => {
                return needsSyncUtil(
                    syncType,
                    syncTypes,
                    onlySyncModifiedData,
                    this.hasModifiedEntitiesToSync.bind(this)
                );
            };
            const syncErrorDict: { [syncKey: string]: Error | IApiError | ITransportError } = {};
            let progressCb: TSyncCallback = (type, errorObject?, syncLogger?) => {
                itemsSynced += 1;
                if (errorObject) {
                    logger.log(logger.Source.SYNC, `Problem syncing '${ type }': ${ errorObject.message }`, apiUser);
                    syncErrorDict[type] = errorObject;
                } else {
                    if (syncLogger) {
                        logger.event(logger.Event.SYNCCOMPLETE, syncLogger.getSyncSummary(type), apiUser);
                    }
                    logger.log(logger.Source.SYNC, `[${ itemsSynced }/${ syncCount }] Synced ${ type }`, apiUser);
                }
                syncLogger?.resetState();

                onProgress && onProgress(type, itemsSynced, syncCount, errorObject);
            };

            const syncComplete = () => {
                if (handleSyncCompletion) {
                    const syncErrorKeys = Object.keys(syncErrorDict);
                    if (syncErrorKeys.length) {
                        const errorMessageParts: string[] = [];
                        syncErrorKeys.forEach(key => errorMessageParts.push(`${ key }:${ syncErrorDict[key].message }`));
                        logger.error(logger.Source.SYNCERROR, `Syncs that were not completed: [${ errorMessageParts.join(",") }]`, apiUser);
                    }
                    dispatcher.trigger(DispatcherEvents.SyncComplete);
                }
                resolve(syncErrorDict);
            };

            if (!apiUserId) {
                syncComplete();
                return;
            }

            if (this.securityService.can("viewShipments", apiUserId) && needsSync(SyncType.Shipments) && hasVehicle) {
                syncMethods.push(() => this.shipmentSync.synchronize(progressCb, force, this.profilingBatcher));
            }

            if (this.securityService.can("getMessages", apiUserId) && needsSync(SyncType.Messages)) {
                syncMethods.push(() => this.messagesSync.synchronize(progressCb, this.profilingBatcher));
            }

            if (needsSync(SyncType.DriverChange)) {
                syncMethods.push(() => this.driverChangeSync.synchronize(progressCb, force, this.profilingBatcher));
            }

            if (this.featureFlagsService.hasFeatureFlag(FeatureFlagName.ENABLE_COACHING_MODULE)) {
                if (needsSync(SyncType.Coaching) && this.hasPendingCoachingWork(apiUserId)) {
                    syncMethods.push(() => this.coachingSync.synchronize(this.profilingBatcher)
                        .then(([type, errorObject]) => progressCb(type, errorObject)));
                }
            }

            syncCount += this.syncDvirAndTrailers(apiUserId, syncMethods, needsSync, hasVehicle, this.profilingBatcher, progressCb, force);

            syncCount += this.syncHos(apiUserId, syncMethods, needsSync, this.profilingBatcher, progressCb, force, syncSource);

            syncCount += this.syncTrips(apiUserId, syncMethods, needsSync, this.profilingBatcher, progressCb);

            syncCount += syncMethods.length;
            if (syncMethods.length === 0) {
                onProgress && onProgress("", itemsSynced, syncCount);
                logger.log(logger.Source.SYNC, "Nothing needs to be synced.", apiUser);
                syncComplete();
                return;
            }

            logger.time(logger.Source.SYNC, "sync-timer");

            // Execute each sync method (to start them all at same time), with an applied timeout, and catching errors
            let syncPromises: Promise[] = syncMethods.map(method => method().timeout(syncTimeout).catch(e => e));

            Promise.all(syncPromises).then(() => {
                if (needsSync(SyncType.UserHosRuleSet) || needsSync(SyncType.HOS)) {
                    let activeUser = this.userService.getCurrentUser();
                    if (apiUser && activeUser && apiUser.id === activeUser.id) {
                        dispatcher.trigger(DispatcherEvents.CheckUnverifiedRuleSetLogs);
                    }
                }

                if (needsSync(SyncType.HOS)) {
                    // Always gets here, even after individual syncPromises' rejection
                    let logs = this.dutyStatusLogService.getHOSLogs(false, apiUserId as string);
                    let addressLookups: IAddressSearch[] = [];
                    let standardStatusLogs = logs.filter(hosLog => hosLog.isStandardStatusDriverLog());

                    standardStatusLogs.forEach(log => {
                        if (log.location?.location && log.isValidLocation() && (!log.location.address || !log.location.address.formattedAddress)) {
                            addressLookups.push({
                                id: log.id,
                                coordinate: log.location.location
                            });
                        }
                    });
                    void populateAddressesRedux(this.api, this.store, addressLookups, false).then(() => {
                        logger.log(logger.Source.SYNC, `Retrieved addresses for ${ addressLookups.length } HOS logs`, this.api.getUser());
                    });
                }

                if (needsSync(SyncType.DVIR)) {
                    // Also populate DVIR addresses
                    let dvirLogs = this.dvirLogsService.getDvirLogs(apiUserId);
                    let dvirAddressLookups: IAddressSearch[] = [];
                    dvirLogs.forEach(dvirLog => {
                        let location = dvirLog.getLocation();
                        let logLocation = (location && location.location) || (dvirLog._tempLocation && dvirLog._tempLocation.location);
                        if (logLocation && !(logLocation.x === 0 && logLocation.y === 0)) {
                            dvirAddressLookups.push({
                                id: dvirLog.id,
                                coordinate: logLocation
                            });
                        }
                    });
                    void populateAddressesRedux(this.api, this.store, dvirAddressLookups, true).then(() => {
                        logger.log(logger.Source.SYNC, `Retrieved addresses for ${ dvirAddressLookups.length } DVIR logs`, this.api.getUser());
                    });
                }

                logger.timeEnd(logger.Source.SYNC, "sync-timer", "Synced", apiUser);
                this.profilingBatcher.log();
                syncComplete();
            }).catch((error) => {
                // Only falls into here if error in the "then" callback above
                logger.error(logger.Source.SYNC, error.message, apiUser);
                this.profilingBatcher.log();
                syncComplete();
            });
        });
    }

}